import { withCrownNormals } from "./geometry/crown-normals.ts";
import { BufferAttribute, BufferGeometry, type Mesh, type Object3D } from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { MODEL_BUNDLES, MODELS, type ModelId } from "../assets/models.generated.ts";
import { meanLuma, tintColors, type ColorAt } from "./tint.ts";

/** Atributos de cor por paleta, na ordem de `MODELS[id].palettes` (F42). */
export const PALETTE_ATTRIBUTES = ["color", "color1", "color2"] as const;

/**
 * Modelos importados (F37, ADR 0009): pacotes GLB gerados por `tools/assets`, com a cor da textura
 * gravada nos vértices e sem materiais. Cada malha `<id>@<lod>` vira uma BufferGeometry com
 * `position`, `color` e `wind`, o mesmo formato do kit de malhas procedurais, mais `color1` e
 * `color2` quando o modelo tem outras paletas (F42: a mesma geometria, outra colorsheet), e `tint` nos
 * itens (F47: 1 nas partes que pegam a cor do tier). Se algo falhar, a biblioteca fica vazia e o jogo
 * usa as malhas procedurais: modelos são só visuais.
 */
export class ModelLibrary {
  private readonly geometries: ReadonlyMap<string, BufferGeometry>;
  /** Vistas de uma paleta só (`color` = a paleta pedida) ou tingidas (F47), criadas sob demanda. */
  private readonly views = new Map<string, BufferGeometry>();

  private constructor(geometries: ReadonlyMap<string, BufferGeometry>) {
    this.geometries = geometries;
  }

  static empty(): ModelLibrary {
    return new ModelLibrary(new Map());
  }

  /** Para testes: geometrias já prontas, com chave `<id>@<lod>`. */
  static fromGeometries(geometries: ReadonlyMap<string, BufferGeometry>): ModelLibrary {
    return new ModelLibrary(geometries);
  }

  /** Lê um pacote GLB e devolve as geometrias por `<id>@<lod>`. */
  static async parse(buffer: ArrayBuffer): Promise<Map<string, BufferGeometry>> {
    // O fallback de SPA do Workers devolve o index.html (200) para arquivo ausente: confere a assinatura.
    if (buffer.byteLength < 12 || new TextDecoder().decode(new Uint8Array(buffer, 0, 4)) !== "glTF") throw new Error("pacote de modelos inválido (não é GLB)");
    const gltf = await new GLTFLoader().parseAsync(buffer, "");
    const out = new Map<string, BufferGeometry>();
    gltf.scene.traverse((object: Object3D) => {
      const mesh = object as Mesh;
      if (!mesh.isMesh) return;
      const geometry = mesh.geometry;
      // O GLTFLoader troca `_WIND` por `_wind` e `COLOR_1` por `color_1`; os shaders leem `wind`, `color1`…
      for (const [from, to] of [["_wind", "wind"], ["_tint", "tint"], ["color_1", "color1"], ["color_2", "color2"]] as const) {
        const attribute = geometry.getAttribute(from);
        if (!attribute) continue;
        geometry.setAttribute(to, attribute);
        geometry.deleteAttribute(from);
      }
      // Malha neon (F59): sem índice, cada triângulo com os próprios 3 vértices. O pacote continua
      // indexado (o LOD do build precisa); desindexa só aqui.
      // Fase 14: as copas ganham a normal de moita (`aCrown`), para sombrear redondas.
      const flat = withCrownNormals(geometry.index ? geometry.toNonIndexed() : geometry);
      if (flat !== geometry) geometry.dispose();
      flat.computeBoundingSphere();
      (mesh.material as { dispose(): void }).dispose();
      out.set(mesh.name, flat);
    });
    return out;
  }

  /**
   * Geometria do modelo no LOD, com todas as paletas (`color` é a padrão). Com `palette`, uma vista
   * em que `color` é essa paleta (divide os buffers com a original); paleta que o modelo não tem
   * cai na padrão.
   */
  get(id: ModelId, lod: number, palette?: string): BufferGeometry | undefined {
    const geometry = this.geometries.get(`${id}@${lod}`);
    const index = palette === undefined ? 0 : (MODELS[id].palettes as readonly string[]).indexOf(palette);
    const attribute = index > 0 ? geometry?.getAttribute(PALETTE_ATTRIBUTES[index]!) : undefined;
    if (!geometry || !attribute) return geometry;
    const key = `${id}@${lod}#${palette}`;
    let view = this.views.get(key);
    if (!view) {
      view = new BufferGeometry();
      for (const name of ["position", "wind", "aCrown"]) {
        const attribute = geometry.getAttribute(name);
        if (attribute) view.setAttribute(name, attribute);
      }
      view.setAttribute("color", attribute);
      view.setIndex(geometry.index);
      view.boundingSphere = geometry.boundingSphere;
      this.views.set(key, view);
    }
    return view;
  }

  /**
   * Vista do modelo no LOD com as partes tingíveis (atributo `tint`, F47) na cor `hex` (sRGB),
   * mantendo o claro e o escuro do atlas: `mix(c, cor·luma(c)/lumaRef, máscara)`, com `lumaRef` a
   * luminância média das partes tingíveis no LOD 0 (essa média vira a cor pedida, igual na mão e no
   * chão). Onde o claro passaria de 1 num canal, a cor inteira é limitada pelo maior canal: o brilho
   * satura sem mudar o matiz (o raio não puxa para o ciano). Divide posição, índices e vento com o modelo; só `color` é nova. `whole`: o modelo inteiro
   * é tingível (sem máscara: a pedra dos itens é a pedrinha dos enfeites). `reference`: o modelo de onde
   * sai o `lumaRef` (F48: a tampa do baú usa o do corpo, e as ferragens das duas peças batem). Sem parte
   * tingível, devolve o próprio modelo.
   */
  tinted(id: ModelId, lod: number, hex: string, whole = false, reference: ModelId = id): BufferGeometry | undefined {
    const geometry = this.geometries.get(`${id}@${lod}`);
    if (!geometry) return undefined;
    const key = `${id}@${lod}~${hex}${whole ? "*" : ""}${reference === id ? "" : `<${reference}`}`;
    const cached = this.views.get(key);
    if (cached) return cached;
    const mask = (g: BufferGeometry) => {
      const attribute = g.getAttribute("tint");
      return (i: number) => (whole ? 1 : attribute ? attribute.getX(i) : 0);
    };
    const base = this.geometries.get(`${reference}@0`) ?? geometry;
    const rgb = (g: BufferGeometry): ColorAt => {
      const color = g.getAttribute("color");
      return (i) => [color.getX(i), color.getY(i), color.getZ(i)];
    };
    const lumaRef = meanLuma(base.getAttribute("color").count, rgb(base), mask(base));
    if (lumaRef === undefined) return geometry;
    const out = tintColors(geometry.getAttribute("color").count, rgb(geometry), mask(geometry), hex, lumaRef);
    const view = new BufferGeometry();
    // Tudo menos as cores (as outras paletas não valem para a vista): posição, vento e máscara divididos.
    for (const [name, attribute] of Object.entries(geometry.attributes)) if (!(PALETTE_ATTRIBUTES as readonly string[]).includes(name)) view.setAttribute(name, attribute);
    view.setAttribute("color", new BufferAttribute(out, 3));
    view.setIndex(geometry.index);
    view.boundingSphere = geometry.boundingSphere;
    this.views.set(key, view);
    return view;
  }

  get size(): number {
    return this.geometries.size;
  }
}

export interface ModelStats {
  ok: boolean;
  ms: number;
  bytes: number;
  geometries: number;
  error?: string;
}

/** Prazo do download inteiro (corpo incluído): o servidor expulsa quem não confirma o carregamento em 30 s. */
export const MODEL_LOAD_TIMEOUT_MS = 15_000;

type Fetch = (url: string, init: { signal: AbortSignal }) => Promise<Pick<Response, "ok" | "status" | "arrayBuffer">>;

/**
 * Carrega os pacotes uma vez por página; `preload()` é idempotente e nunca rejeita. Erro de rede,
 * arquivo inválido ou estouro do prazo deixam a biblioteca vazia (malhas procedurais).
 */
export class ModelLoader {
  library = ModelLibrary.empty();
  private pending: Promise<void> | undefined;
  private status: ModelStats = { ok: false, ms: 0, bytes: 0, geometries: 0 };
  private readonly fetch: Fetch;
  private readonly timeoutMs: number;

  constructor(fetch: Fetch = (url, init) => globalThis.fetch(url, init), timeoutMs = MODEL_LOAD_TIMEOUT_MS) {
    this.fetch = fetch;
    this.timeoutMs = timeoutMs;
  }

  preload(bundles: Readonly<Record<string, string>> = MODEL_BUNDLES): Promise<void> {
    this.pending ??= this.load(bundles);
    return this.pending;
  }

  stats(): ModelStats {
    return { ...this.status };
  }

  private async load(bundles: Readonly<Record<string, string>>): Promise<void> {
    const start = performance.now();
    const abort = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        abort.abort();
        reject(new Error(`tempo esgotado (${this.timeoutMs} ms)`));
      }, this.timeoutMs);
    });
    const work = this.fetchAll(bundles, abort.signal);
    // Depois do prazo, o trabalho abortado ainda pode rejeitar: sem rejeição solta.
    work.catch(() => undefined);
    try {
      const { geometries, bytes } = await Promise.race([work, timeout]);
      this.library = ModelLibrary.fromGeometries(geometries);
      this.status = { ok: true, ms: Math.round(performance.now() - start), bytes, geometries: geometries.size };
    } catch (error) {
      console.warn("modelos indisponíveis; usando malhas procedurais", error);
      this.status = { ok: false, ms: Math.round(performance.now() - start), bytes: 0, geometries: 0, error: String(error) };
    } finally {
      clearTimeout(timer);
    }
  }

  private async fetchAll(bundles: Readonly<Record<string, string>>, signal: AbortSignal): Promise<{ geometries: Map<string, BufferGeometry>; bytes: number }> {
    const geometries = new Map<string, BufferGeometry>();
    let bytes = 0;
    for (const url of Object.values(bundles)) {
      const response = await this.fetch(url, { signal });
      if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
      const buffer = await response.arrayBuffer();
      bytes += buffer.byteLength;
      for (const [key, geometry] of await ModelLibrary.parse(buffer)) geometries.set(key, geometry);
    }
    return { geometries, bytes };
  }
}

export const models = new ModelLoader();
